/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '../../UNSAFE_Axis/axis.types';
import {
  DataCursorPosition,
  DataCursorStyle,
  onDataCursorPositionChange
} from './DataCursor.types';
import { getDataCursorCoords } from './util';
import { styles } from './themes/DataCursorStyles.css';
import { Scale } from '../../UNSAFE_Chart';
import { Info } from '../../UNSAFE_Chart';

type Props = {
  findNearest: (x: number, y: number) => any;
  dataCursorPosition?: DataCursorPosition;
  isDataCursorEnabled?: boolean;
  isHoriz: boolean;
  plotAreaSpace: Dimension;
  yScale: Scale;
  xScale: Scale;
  onDataCursorPositionChange?: onDataCursorPositionChange;
  dataCursorStyle?: DataCursorStyle;
  focusedItemInfo: Info;
  getDataItemPos: (seriesIndex: number, groupIndex: number) => Dimension | undefined;
};

export function useChartDataCursor({
  findNearest,
  dataCursorPosition,
  onDataCursorPositionChange,
  isDataCursorEnabled,
  isHoriz,
  plotAreaSpace,
  yScale,
  xScale,
  dataCursorStyle,
  focusedItemInfo,
  getDataItemPos
}: Props) {
  let dataCursorContent = null;
  const x = dataCursorPosition?.x;
  const isSnap = dataCursorStyle?.behavior === 'snap';

  const updateDataCursor = () => {
    const { seriesIndex, groupIndex } = focusedItemInfo;
    const elemBounds = getDataItemPos(seriesIndex!, groupIndex!);
    if (!elemBounds) {
      return;
    }
    const px = elemBounds?.x + elemBounds.width / 2;
    const py = elemBounds?.y + elemBounds.height / 2;

    let X = xScale.invert(isHoriz ? py : px);
    let Y = yScale.invert(isHoriz ? px : py);

    const neareastItemInfo = findNearest(X, Y);
    if (isSnap) {
      X = xScale.invert(neareastItemInfo.xCoord);
      Y = yScale.invert(neareastItemInfo.yCoord);
    }
    onDataCursorPositionChange?.({ x: X, y: Y });
  };

  const onPointerMove = (event: PointerEvent) => {
    const px = event.offsetX;
    const py = event.offsetY;
    if (
      px < plotAreaSpace.x ||
      py < plotAreaSpace.y ||
      px > plotAreaSpace.x + plotAreaSpace.width ||
      py > plotAreaSpace.y + plotAreaSpace.height
    ) {
      dataCursorContent = null;
      onDataCursorPositionChange?.({ x: undefined, y: undefined });
      return;
    }

    let X = xScale.invert(isHoriz ? py : px);
    let Y = yScale.invert(isHoriz ? px : py);

    const neareastItemInfo = findNearest(X, Y);
    if (isSnap) {
      X = xScale.invert(neareastItemInfo.xCoord);
      Y = yScale.invert(neareastItemInfo.yCoord);
    }
    onDataCursorPositionChange?.({ x: X, y: Y });
  };

  const onPointerLeave = () => {
    onDataCursorPositionChange?.({ x: undefined, y: undefined });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      onDataCursorPositionChange?.({ x: undefined, y: undefined });
      return;
    }
    updateDataCursor();
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Tab' && focusedItemInfo.isCurrent) {
      return;
    }
    updateDataCursor();
  };

  if (x != undefined && isDataCursorEnabled) {
    const { mainLineProps, contrastLineProps } = getDataCursorCoords(
      xScale,
      x,
      plotAreaSpace,
      isHoriz,
      dataCursorStyle
    );

    dataCursorContent = (
      <g class={styles.dataCursor}>
        <line {...mainLineProps} class={styles.dataCursorLine} />
        <line {...contrastLineProps} class={styles.contrastLine} />
      </g>
    );
  }

  return {
    dataCursorContent,
    dataCursorProps: isDataCursorEnabled
      ? {
          onPointerMove,
          onPointerLeave,
          onKeyDown,
          onKeyUp
        }
      : {}
  };
}
